import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Code,
  Database,
  DollarSign,
  ExternalLink,
  Key,
  Target,
  TrendingUp,
} from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { partnerApi } from '../../../lib/api';

interface AcquireMetricsData {
  summary: {
    activeSmbs: number;
    averageSmbSpend: number;
    totalSpend: number;
  };
  dailySpend: Array<{
    smbName: string;
    platformName: string;
    adsSpend: number;
    cpc: number;
    revenue: number;
    roas: number;
  }>;
  needsSetup: Array<{
    businessName: string;
    platformName: string;
    website: string | null;
    dataIntegration: string;
    websiteTracking: string;
    connectedAdAccounts: number;
    adPlatforms: string;
    firstConnectedAt: string;
  }>;
}

export const AcquireMetrics = ({ selectedDays }: { selectedDays: number }) => {
  const {
    data: metrics,
    isLoading,
    isFetching,
  } = useQuery<AcquireMetricsData>({
    queryKey: ['acquire-metrics', selectedDays],
    queryFn: () => partnerApi.getAcquireMetrics(selectedDays),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: previousData => previousData,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading metrics...</div>
      </div>
    );
  }

  if (!metrics) {
    const steps = [
      {
        title: 'Credentials & Frontend',
        description: 'Get your API credentials and embed the Reach UI into your platform',
        href: 'https://docs.embedreach.com/onboarding/credentials',
        icon: <Key className="h-4 w-4" />,
      },
      {
        title: 'Data Sync',
        description: 'Create schemas and sync customer and transaction data',
        href: 'https://docs.embedreach.com/onboarding/data-sync',
        icon: <Database className="h-4 w-4" />,
      },
      {
        title: 'Tracking Snippet',
        description: 'Install the attribution snippet on your tenant websites',
        href: 'https://docs.embedreach.com/onboarding/tracking',
        icon: <Code className="h-4 w-4" />,
      },
    ];

    return (
      <div className="space-y-6">
        <Card className="p-8">
          <div className="max-w-lg">
            <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
              <Target className="h-5 w-5 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Get started with Acquire</h3>
            <p className="text-sm text-gray-500 mb-6">
              Acquire gives your tenants AI-powered ad campaign creation and management for Google and
              Meta. Complete these steps to start seeing metrics.
            </p>
            <div className="space-y-3">
              {steps.map((step, i) => (
                <a
                  key={step.title}
                  href={step.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors group">
                  <span className="text-gray-400 group-hover:text-gray-600 mt-0.5">{step.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      <span className="text-gray-400 text-xs">{i + 1}.</span>
                      {step.title}
                      <ExternalLink className="h-3 w-3 text-gray-400" />
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div
        className={`grid grid-cols-1 lg:grid-cols-3 gap-4 transition-opacity duration-200 ${isFetching ? 'opacity-75' : 'opacity-100'}`}>
        {/* Active Acquire Tenants */}
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Acquire Tenants</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{metrics.summary.activeSmbs}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>

        {/* Average Tenant Spend */}
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Tenant Spend</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatCompactCurrency(metrics.summary.averageSmbSpend)}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>

        {/* Total Spend */}
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Acquire Spend ({selectedDays === 0 ? 'All Time' : `Last ${selectedDays}d`})
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatCompactCurrency(metrics.summary.totalSpend)}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Daily Spend Table */}
      <Card
        className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Acquire Performance by Tenant ({selectedDays === 0 ? 'All Time' : `${selectedDays} Days`})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ads Spend
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ROAS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics.dailySpend && metrics.dailySpend.length > 0 ? (
                metrics.dailySpend.map((spend, index) => (
                  <tr key={`${spend.smbName}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {spend.smbName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(spend.adsSpend)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(spend.cpc)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(spend.revenue)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {spend.roas.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    No Acquire campaign data available for this time period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Tenants Who Need Setup Table */}
      <Card
        className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Tenants Who Need Setup</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Website
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Integration
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Website Tracking
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connected Ad Accounts
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ad Platforms
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  First Connected At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics.needsSetup.length > 0 ? (
                metrics.needsSetup.map((business, index) => (
                  <tr key={`${business.businessName}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.businessName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.platformName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.website || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          business.dataIntegration === 'Set'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        {business.dataIntegration}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          business.websiteTracking === 'Set'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        {business.websiteTracking}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.connectedAdAccounts}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.adPlatforms}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.firstConnectedAt}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                    All tenants are fully set up
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
